import React, { useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, List, ListOrdered, ImageIcon, Link2, Send, Loader2, Clock, RotateCw,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../lib/toast';
import { TextInputDialog } from './ConfirmDialog';
import { sanitizeRichText } from '../lib/richText';
import { formatClock } from '../lib/editorTime';

// ─── Types ───────────────────────────────────────────────────────────────────

interface RichCommentEditorProps {
  videoId: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** `timeSeconds`: the attached video moment, or null. */
  onSubmit: (html: string, timeSeconds: number | null) => Promise<void>;
  onFocus?: () => void;
  /** Current player position; when given, the comment can be attached to that moment. */
  getTime?: () => number | null;
}

// ─── HTML sanitizer ──────────────────────────────────────────────────────────
// Only the tags TipTap produces, with no attributes except safe link/image addresses.
export const sanitizeCommentHtml = (html: string): string => sanitizeRichText(html, { comment: true });

// ─── Toolbar button ───────────────────────────────────────────────────────────

const ToolbarBtn: React.FC<{
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    style={{
      padding: '4px 6px',
      borderRadius: 6,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? 'var(--surfaceSelected, #ede9fe)' : 'transparent',
      color: active
        ? 'var(--surfaceSelectedForeground, #4f46e5)'
        : 'var(--textSecondary, #6b7280)',
      opacity: disabled ? 0.45 : 1,
      display: 'flex',
      alignItems: 'center',
    }}
  >
    {children}
  </button>
);

// ─── Main component ──────────────────────────────────────────────────────────

const RichCommentEditor: React.FC<RichCommentEditorProps> = ({
  videoId,
  placeholder = 'Añade un comentario…',
  disabled = false,
  loading = false,
  onSubmit,
  onFocus,
  getTime,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [uploadingImage, setUploadingImage] = React.useState(false);
  // TipTap v3 doesn't re-render this component on every keystroke, so reading
  // editor.getText() during render goes stale. Track emptiness from editor events instead.
  const [isEmpty, setIsEmpty] = React.useState(true);
  const submittingRef = useRef(false);
  // Video moment, taken when the viewer starts typing; ticked when past 0:00.
  const [stamp, setStamp] = React.useState<{ seconds: number; on: boolean } | null>(null);
  const readStamp = () => {
    const seconds = getTime?.();
    return seconds == null || !Number.isFinite(seconds) ? null : { seconds: Math.floor(seconds), on: seconds >= 1 };
  };
  useEffect(() => {
    if (!getTime) return;
    if (isEmpty) setStamp(null);
    else setStamp((s) => s ?? readStamp());
  }, [isEmpty]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Image upload helper ────────────────────────────────────────────────────
  const handleImageUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setUploadingImage(true);
    try {
      const { data, error } = await apiClient.uploadCommentImage(file, videoId);
      if (error || !data?.url) {
        console.error('[RichCommentEditor] Image upload failed:', error);
        toast.error(t('comment_image_failed', 'Could not add the image. Use a JPEG, PNG, GIF or WebP up to 5 MB.'));
        return;
      }
      editor?.chain().focus().setImage({ src: data.url, alt: file.name }).run();
    } finally {
      setUploadingImage(false);
    }
  }, [videoId, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── TipTap editor ─────────────────────────────────────────────────────────
  // Nothing to post: no text and no image.
  const isBlank = (e: { getText: () => string; getHTML: () => string }) => !e.getText().trim() && !/<img\s/i.test(e.getHTML());

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    editorProps: {
      // Intercept image paste
      handlePaste(_, event) {
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageItem = items.find((i) => i.type.startsWith('image/'));
        if (!imageItem) return false;
        event.preventDefault();
        const file = imageItem.getAsFile();
        if (file) handleImageUpload(file);
        return true;
      },
      // Intercept image drop
      handleDrop(_, event) {
        const files = Array.from(event.dataTransfer?.files ?? []);
        const imageFile = files.find((f) => f.type.startsWith('image/'));
        if (!imageFile) return false;
        event.preventDefault();
        handleImageUpload(imageFile);
        return true;
      },
    },
    onCreate({ editor: e }) { setIsEmpty(isBlank(e)); },
    onUpdate({ editor: e }) { setIsEmpty(isBlank(e)); },
    onFocus() { onFocus?.(); },
    editable: !disabled,
  });

  // Keep editable in sync with disabled prop
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // Same rules for the button and Ctrl+Enter; the ref blocks a second submit before re-render.
  const handleSubmit = async () => {
    if (!editor || disabled || loading || uploadingImage || submittingRef.current) return;
    const html = editor.getHTML();
    if (isBlank(editor)) return; // only whitespace / no content
    submittingRef.current = true;
    try {
      await onSubmit(html, stamp?.on ? stamp.seconds : null);
      editor.commands.clearContent();
      setIsEmpty(true);
    } catch {
      // Not posted (the parent showed why): keep the text so it can be sent again.
    } finally {
      submittingRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl/Cmd + Enter submits
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Link URL is asked for in an in-app dialog (not window.prompt).
  const [showLinkDialog, setShowLinkDialog] = React.useState(false);
  const insertLink = () => setShowLinkDialog(true);

  const applyLink = (raw: string) => {
    setShowLinkDialog(false);
    if (!editor) return;
    // "example.com" → "https://example.com"; only web and mail links are allowed.
    const href = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
    if (!/^(https?:|mailto:)/i.test(href)) {
      toast.error(t('comment_link_invalid', 'Enter a valid web address.'));
      editor.commands.focus();
      return;
    }
    if (editor.state.selection.empty) {
      // Nothing selected: insert the address itself as the link text.
      editor.chain().focus()
        .insertContent({ type: 'text', text: raw, marks: [{ type: 'link', attrs: { href } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
  };

  const isSubmitting = loading || uploadingImage;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="rich-comment-editor"
      style={{
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 12,
        background: 'var(--card, #fff)',
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}
      // Highlight this editor (not the first one on the page, e.g. when replying).
      onFocus={(e) => {
        e.currentTarget.style.setProperty('border-color', 'var(--primary, #6366f1)');
      }}
      onBlur={(e) => {
        e.currentTarget.style.setProperty('border-color', 'var(--border, #e5e7eb)');
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '6px 10px',
          borderBottom: '1px solid var(--border, #e5e7eb)',
          background: 'var(--background, #f9fafb)',
          flexWrap: 'wrap',
        }}
      >
        <ToolbarBtn
          onClick={() => editor?.chain().focus().toggleBold().run()}
          active={editor?.isActive('bold')}
          disabled={disabled}
          title={t('comment_editor.bold', 'Bold (Ctrl+B)')}
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          active={editor?.isActive('italic')}
          disabled={disabled}
          title={t('comment_editor.italic', 'Italic (Ctrl+I)')}
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div style={{ width: 1, height: 16, background: 'var(--border, #e5e7eb)', margin: '0 4px' }} />
        <ToolbarBtn
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          active={editor?.isActive('bulletList')}
          disabled={disabled}
          title={t('comment_editor.bullet_list', 'Bulleted list')}
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          active={editor?.isActive('orderedList')}
          disabled={disabled}
          title={t('comment_editor.numbered_list', 'Numbered list')}
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div style={{ width: 1, height: 16, background: 'var(--border, #e5e7eb)', margin: '0 4px' }} />
        <ToolbarBtn onClick={insertLink} active={editor?.isActive('link')} disabled={disabled} title={t('comment_editor.link', 'Insert link')}>
          <Link2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploadingImage}
          title={t('comment_editor.image', 'Insert image')}
        >
          {uploadingImage
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <ImageIcon className="w-3.5 h-3.5" />}
        </ToolbarBtn>

        {/* Emoji quick-insert */}
        <div style={{ width: 1, height: 16, background: 'var(--border, #e5e7eb)', margin: '0 4px' }} />
        {(['❤️', '👍', '🔥', '👏', '👀', '😮'] as const).map((emoji) => (
          <button
            key={emoji}
            type="button"
            disabled={disabled}
            title={`Insertar ${emoji}`}
            aria-label={`Insertar ${emoji}`}
            onClick={() => editor?.chain().focus().insertContent(emoji).run()}
            style={{
              padding: '2px 4px',
              border: 'none',
              background: 'transparent',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: 16,
              lineHeight: 1,
              opacity: disabled ? 0.45 : 1,
              borderRadius: 4,
            }}
          >
            {emoji}
          </button>
        ))}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImageUpload(file);
            e.target.value = '';
          }}
        />

        {/* Submit button — pushed to the right */}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || isEmpty || disabled}
          title={t('comment_editor.post_title', 'Post (Ctrl+Enter)')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 14px',
            borderRadius: 8,
            border: 'none',
            cursor: isSubmitting || isEmpty || disabled ? 'not-allowed' : 'pointer',
            background: 'var(--primary, #6366f1)',
            color: 'var(--button-primary-foreground, #fff)',
            fontSize: 13,
            fontWeight: 500,
            opacity: isSubmitting || isEmpty || disabled ? 0.5 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {isSubmitting
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Send className="w-4 h-4" />}
          {t('comment_editor.post', 'Post')}
        </button>
      </div>

      {/* Editor area */}
      <div onKeyDown={handleKeyDown} style={{ minHeight: 80, maxHeight: 320, overflowY: 'auto' }}>
        <EditorContent editor={editor} className="tiptap-comment-content" />
      </div>

      {/* Moment in the video this comment refers to */}
      {stamp && (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, padding: '6px 10px', borderTop: '1px solid var(--border, #e5e7eb)', fontSize: 12 }}>
          <label
            title={t('comments.time_hint_on', 'Viewers can click the time to jump to this moment')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
              background: stamp.on ? 'var(--surfaceSelected, #ede9fe)' : 'transparent',
              color: stamp.on ? 'var(--surfaceSelectedForeground, #4f46e5)' : 'var(--textSecondary, #6b7280)',
              fontWeight: 500,
            }}
          >
            <input
              type="checkbox"
              checked={stamp.on}
              onChange={(e) => setStamp({ ...stamp, on: e.target.checked })}
              style={{ accentColor: 'var(--primary, #6366f1)', margin: 0 }}
            />
            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{t('comments.time_attach', 'At {{time}}', { time: formatClock(stamp.seconds) })}</span>
          </label>
          <ToolbarBtn
            onClick={() => { const next = readStamp(); if (next) setStamp({ ...next, on: true }); }}
            title={t('comments.time_refresh', 'Use the current time')}
          >
            <RotateCw className="w-3.5 h-3.5" />
          </ToolbarBtn>
          <span style={{ color: 'var(--textSecondary, #6b7280)' }}>
            {stamp.on
              ? t('comments.time_hint_on', 'Viewers can click the time to jump to this moment')
              : t('comments.time_hint_off', 'Optional: tick to link your comment to this moment')}
          </span>
        </div>
      )}

      {showLinkDialog && createPortal(
        <TextInputDialog
          title={t('comment_link_title', 'Insert link')}
          label={t('comment_link_label', 'Web address')}
          placeholder="https://"
          confirmLabel={t('comment_link_confirm', 'Insert')}
          maxLength={2048}
          onConfirm={applyLink}
          onCancel={() => { setShowLinkDialog(false); editor?.commands.focus(); }}
        />,
        document.body
      )}
    </div>
  );
};

export default RichCommentEditor;
